import React, { FC, useEffect, useRef, useState } from 'react';
import './Chat.scss';
import Tag from "../../../components/atoms/Tag/Tag";
import CloseIcon from "../../../components/icons/Chat/CloseIcon";
import AttachIcon from "../../../components/icons/Chat/AttachIcon";
import { useDispatchEx, useSelectorEx } from "../../../hooks/redux";
import { useFormik } from "formik";
import SendIcon from "../../../components/icons/SendIcon";

// Импорты экшенов чата (включая легковесный маркер "прочитано")
import {
    asyncCreateSupportMessageAction,
    asyncGetSupportAction,
    asyncMarkMessagesAsReadAction
} from "../../../redux/slices/supports/supportsAction";

// Импорты для синхронизации счетчиков заявок
import {
    clearUnreadCount,
    updateOrder
} from "../../../redux/slices/orders/ordersSlice";
import {
    asyncGetGlobalUnreadCountAction
} from "../../../redux/slices/orders/ordersAction";

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { io } from "socket.io-client";
import { addSupportMessage } from "../../../redux/slices/supports/supportsSlice";
import { useParams } from "react-router-dom";
import { getFileType, getOrderStatus } from "../../../helpers/functions";
import { createSound } from "../../../helpers/scripts";
import soundNew from '../../../assets/sounds/new.mp3';
import TextAvatar from "../../../components/atoms/TextAvatar/TextAvatar";

const Chat: FC = () => {
    const inputRef = useRef<any>();
    const [url, setUrl] = useState<any[]>([]);
    const [files, setFiles] = useState<any[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);

    const { supports } = useSelectorEx(state => state.supports);
    const { order } = useSelectorEx(state => state.orders);
    const dispatch = useDispatchEx();

    const { values, resetForm, handleChange, handleBlur, handleSubmit } = useFormik({
        initialValues: {
            message: '',
        },
        enableReinitialize: true,
        validationSchema: null,
        onSubmit: (value) => formHandle(value),
    });

    const formatMessageDate = (value: any) => {
        if (!value) return '';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';

        const now = new Date();

        const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        if (isToday) {
            return new Intl.DateTimeFormat('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        }

        return new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const getMessageDate = (message: any) => {
        return formatMessageDate(message.createdAt);
    };

    const formHandle = (values: any) => {
        if (!values.message && !files.length) return;
        dispatch(asyncCreateSupportMessageAction({
            ...values,
            orderID: order?.orderID,
            files,
        }));
        setUrl([]);
        setFiles([]);
        inputRef.current.value = "";
        resetForm();
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    const addFiles = (fileList: FileList | File[]) => {
        const arrayList = Array.from(fileList);
        const imageList: any[] = [];

        for (let value of arrayList) {
            imageList.push({
                url: URL.createObjectURL(value),
                type: value.type,
            });
        }

        setUrl(prevState => [...prevState, ...imageList]);
        setFiles(prevState => [...prevState, ...arrayList]);
    };

    const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const arrayList = Array.from(e.target.files as FileList);
        addFiles(arrayList);
        inputRef.current.value = "";
    };

    const onRemoveFile = (id: number) => {
        setFiles(prevState => prevState.filter((item, index) => index !== id));
        setUrl(prevState => prevState.filter((item, index) => index !== id));
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;

        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;

        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    let { orderID } = useParams();

    // Загрузка истории чата и сброс счетчиков непрочитанных
    useEffect(() => {
        dispatch(asyncGetSupportAction(Number(orderID)))
            .unwrap()
            .then(() => {
                // Как только история получена (бэкенд сделал сообщения прочитанными),
                // мы сбрасываем локальный счетчик этой заявки...
                dispatch(clearUnreadCount(Number(orderID)));

                // ...и запрашиваем актуальный глобальный счетчик, чтобы бейджик в меню обновился.
                dispatch(asyncGetGlobalUnreadCountAction());
            })
            .catch((err) => {
                console.error("Ошибка при загрузке истории чата", err);
            });
    }, [dispatch, orderID]);

    useEffect(() => {
        Fancybox.bind("[data-fancybox]", {});

        return () => {
            Fancybox.destroy();
        };
    }, []);

    // Подключение к сокету
    useEffect(() => {
        if (order?.orderID) {
            const token = localStorage.getItem('token');
            const socket = io(`${process.env.REACT_APP_URL_WSS}`, {
                extraHeaders: {
                    Authorization: `Bearer ${token}`,
                },
                query: {
                    orderID: order.orderID,
                    userToken: '',
                },
            });

            socket.on('receiveSupportMessage', (data: any) => {
                dispatch(addSupportMessage(data));
                createSound(soundNew);

                // Если пришло сообщение от пользователя — легковесно помечаем прочитанным в БД,
                // так как админ прямо сейчас находится в этом чате.
                if (data.fromId === data.orderId) {
                    dispatch(asyncMarkMessagesAsReadAction(Number(orderID)));
                }
            });

            socket.on('receiveOrderMessage', (data: any) => {
                dispatch(updateOrder(data));
            });

            return () => {
                socket.disconnect();
            };
        }
    }, [order?.orderID, dispatch, orderID]);

    const messagesContainerRef = useRef<any>(null);

    // Автоскролл вниз при новых сообщениях
    useEffect(() => {
        if (messagesContainerRef.current) {
            const { scrollHeight, clientHeight } = messagesContainerRef.current;
            messagesContainerRef.current.scrollTop = scrollHeight - clientHeight;
        }
    }, [supports]);

    const getUnitForField = (fieldKey: string): string => {
        if (fieldKey === 'money_from') return 'RUB';
        if (fieldKey === 'money_to') return order?.type_to || '';
        if (fieldKey === 'rate') return order?.type_to ? `RUB = 1 ${order.type_to}` : 'RUB';
        return '';
    };

    return (
        <div
            className={`chat ${isDragging ? 'chat--dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className="chat__drop-overlay">
                    <div className="chat__drop-overlay-content">
                        <AttachIcon />
                        <span>Перетащите файлы сюда</span>
                    </div>
                </div>
            )}

            <div className="chat__info">
                <div className="chat__status">
                    <Tag
                        name={`${getOrderStatus(order?.status ?? 0).name}`}
                        color={getOrderStatus(order?.status ?? 0).color}
                        textColor={getOrderStatus(order?.status ?? 0).textColor}
                    />
                    <span>Статус</span>
                </div>
                {(order?.status && order?.status > 1 && order?.status !== 5) ? (
                    <div className="chat__paid">
                        Оплачено
                    </div>
                ) : ""}
            </div>

            <div className="chat__content" ref={messagesContainerRef}>
                {supports.length ? (
                    supports.map((message, index) => {
                        const isFrom = message.fromId !== message.orderId;
                        const messageDate = getMessageDate(message);

                        // Логи изменений (Ордер отредактирован)
                        if (message.type === 10) {
                            let parsedChanges: Record<string, { old: any; new: any }> = {};

                            try {
                                parsedChanges = JSON.parse(message.message);
                            } catch (e) {
                                parsedChanges = { 'Изменение': { old: 'Старое', new: message.message } };
                            }

                            const fieldTranslations: Record<string, string> = {
                                money_from: 'Сумма отправки',
                                rate: 'Курс',
                                money_to: 'Сумма получения'
                            };

                            return (
                                <div key={index} className="chat__message-item">
                                    <div className="chat__message-log">
                                        <span className="chat__message-log-title">Изменения в заявке:</span>

                                        {Object.entries(parsedChanges).map(([key, change], i) => {
                                            const unit = getUnitForField(key);

                                            return (
                                                <div key={i} className="chat__message-log-item">
                                                    <span className="chat__message-log-name">{fieldTranslations[key] || key}:</span>
                                                    <Tag name={`${String(change.old)} ${unit}`.trim()} color="rgba(209, 0, 0, 0.13)" textColor="#d10000" />
                                                    <span className="chat__message-log-arrow">→</span>
                                                    <Tag name={`${String(change.new)} ${unit}`.trim()} color="rgba(42, 185, 95, 0.13)" textColor="#2ab95f" />
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {messageDate ? (
                                        <div className="chat__message-meta chat__message-meta--outside">
                                            <span className="chat__message-time-inline">{messageDate}</span>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }

                        // Смена статуса
                        if (message.type !== -1) {
                            return (
                                <div key={index} className="chat__message-item">
                                    <div className="chat__message-status">
                                        <div className="chat__message-status-main">
                                            <span>Новый статус</span>
                                            <Tag
                                                name={`${getOrderStatus(message.type ?? 0).name}`}
                                                color={getOrderStatus(message.type ?? 0).color}
                                                textColor={getOrderStatus(message.type ?? 0).textColor}
                                            />
                                        </div>
                                    </div>

                                    {messageDate ? (
                                        <div className="chat__message-meta chat__message-meta--outside">
                                            <span className="chat__message-time-inline">{messageDate}</span>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }

                        // Обычные сообщения
                        return (
                            <div key={index} className="chat__message-item">
                                {message.message ? (
                                    <div className={`chat__message ${isFrom ? 'chat__message--from' : ''}`}>
                                        <span className="chat__message-text">{message.message}</span>
                                        {messageDate ? (
                                            <span className="chat__message-time-inline">{messageDate}</span>
                                        ) : null}
                                    </div>
                                ) : null}

                                {message.attachedFiles ? (
                                    <div className={`chat__message-images-wrap ${isFrom ? 'chat__message-images-wrap--from' : ''}`}>
                                        <div className={`chat__message-images ${isFrom ? 'chat__message-images--from' : ''}`}>
                                            {message.attachedFiles
                                                .split(',')
                                                .filter(attach => attach)
                                                .map((attach, attachIndex) => (
                                                    <div key={attachIndex} className={'chat__form-img'}>
                                                        {getFileType(attach) === 'pdf' ? (
                                                            <a target={'_blank'} rel="noreferrer" href={`${process.env.REACT_APP_URL_SERVER}/${attach}`}>
                                                                <TextAvatar name={'PDF'} />
                                                            </a>
                                                        ) : (
                                                            <a href={`${process.env.REACT_APP_URL_SERVER}/${attach}`} data-fancybox="gallery">
                                                                <img src={`${process.env.REACT_APP_URL_SERVER}/${attach}`} alt="" />
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>

                                        {!message.message && messageDate ? (
                                            <div className="chat__message-meta">
                                                <span className="chat__message-time-inline">{messageDate}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                ) : ""}
            </div>

            <div className="chat__form">
                <div className="chat__form-images">
                    {url.length ? (
                        url.map((item, index) => {
                            const isPdf = item?.type === 'application/pdf';

                            if (isPdf) {
                                return (
                                    <div key={index} className="chat__form-img">
                                        <TextAvatar name={'PDF'} />
                                        <div className="chat__form-controls">
                                            <a onClick={() => onRemoveFile(index)}><CloseIcon /></a>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={index} className="chat__form-img">
                                    <img src={item?.url} alt="" />
                                    <div className="chat__form-controls">
                                        <a onClick={() => onRemoveFile(index)}><CloseIcon /></a>
                                    </div>
                                </div>
                            );
                        })
                    ) : ""}
                </div>

                <div className="chat__form-wrapper">
                    <div className="chat__form-attach">
                        <AttachIcon />
                        <input
                            ref={inputRef}
                            name={'files'}
                            type="file"
                            multiple={true}
                            accept=".jpg, .jpeg, .png, .webp, .pdf, image/jpeg, image/png, image/webp, application/pdf"
                            onChange={onChangeFile}
                        />
                    </div>

                    <div className="chat__form-textarea">
                        <input
                            autoComplete={'off'}
                            onKeyDown={handleKeyDown}
                            value={values.message}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            name={'message'}
                            placeholder={'Написать сообщение'}
                            type="text"
                        />
                    </div>

                    <div className={'chat__form-button'}>
                        <button onClick={() => handleSubmit()} className={'chat__form-btn'} type={'submit'}>
                            <SendIcon />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chat;